import { z } from 'zod';

export const TimelyAllowedSideEffectSchema = z.enum([
  'project_templates',
  'archived_client_import',
]);
export type TimelyAllowedSideEffect = z.infer<typeof TimelyAllowedSideEffectSchema>;

export const TIMELY_ALLOWED_SIDE_EFFECT_PROJECT_TEMPLATES =
  'project_templates' satisfies TimelyAllowedSideEffect;

/**
 * TT-5643: lets a project import under a client the account archived in Timely.
 * Timely otherwise rejects such a project ("company_id has been archived"),
 * which also takes out every task under it. The client itself is NOT restored —
 * it stays archived, and the project imports archived alongside it.
 *
 * Applied to the project rows of ONE export, by the dispatch of a human-triggered
 * sync — never stored on the resource itself. Resource metadata is replaced on
 * every upsert and outlives the export that set it, so a permission kept there
 * could both be erased before use and leak to a later background export.
 */
export const TIMELY_ALLOWED_SIDE_EFFECT_ARCHIVED_CLIENT_IMPORT =
  'archived_client_import' satisfies TimelyAllowedSideEffect;

const KNOWN_SIDE_EFFECTS: readonly string[] = TimelyAllowedSideEffectSchema.options;

/**
 * Unknown values are DROPPED on read, never rejected.
 *
 * This schema parses `resource.metadata` that an EARLIER deploy wrote, so this
 * list can gain members safely but must never lose one: removing a member is a
 * breaking change against rows already in the database. Renaming this branch's
 * side effect from `unarchive` to `archived_client_import` did exactly that —
 * every stored row still carrying `unarchive` failed
 * `Failed to parse connection details` on load, which killed the whole sync for
 * that connection and took Trello, ClickUp and Monday down together. One stale
 * string in one resource must never be able to stop a sync.
 *
 * Dropping unknowns is self-healing: `upsert_resource` overwrites metadata
 * wholesale, so a stale value disappears on the row's next import with no
 * cleanup step.
 *
 * KNOWN TRADE-OFF: this schema is also the store-time gate (`BaseProvider`'s
 * validation and `prepareResourceRows`), so it is NOT read-only lenient — a
 * provider that writes a mistyped member now stores `allowed_side_effects: []`
 * where it previously threw. That is deliberate: reverting the write path to a
 * strict enum reopens the parse failure above, which takes a whole connection's
 * sync down rather than silently dropping one provider's typo. Splitting into a
 * strict write schema and a lenient read schema would get both, and is the right
 * follow-up if a provider ever needs to fail loudly on its own metadata.
 */
export const TimelyAllowedSideEffectsSchema = z
  .preprocess(
    value =>
      Array.isArray(value) ? value.filter(item => KNOWN_SIDE_EFFECTS.includes(item)) : value,
    z.array(TimelyAllowedSideEffectSchema)
  )
  .describe('Timely side effects this resource is explicitly allowed to trigger');
